// NightGuardData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const nightGuard: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Night Guard for Teeth | Elite Body Home",
            "description": "Custom-made night guards to protect your teeth from grinding and clenching. Prevent tooth damage, reduce jaw pain, and improve sleep quality.",
            "robots": "index, follow",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Night Guard"],
        "hero": {
            "title": "Night Guard for Teeth",
            "subtitle": "Protect Your Smile, Even While You Sleep",
            "description": "At Elite Body Home, our custom-made night guards are designed to safeguard your teeth from the damaging effects of grinding and clenching while you sleep. Crafted for comfort and precision, these guards help prevent wear and tear on your teeth, reduce jaw pain, and promote a peaceful night's rest."
        },
        "howItWorks": {
            "title": "What is a Night Guard?",
            "description": "Your night guard is a dental device that fits over your teeth and should be worn while sleeping for protection against the effects of grinding or clenching your teeth. It provides a buffer, absorbing the pressure and preventing the upper and lower teeth from touching directly."
        },
        "procedureSteps": {
            "heading": "Steps For Dental Night Guard Procedure",
            "para": "Key Points:",
            "steps": [
                {
                    "title": "Indication",
                    "desc": "It prevents bruxism damage to teeth.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Custom fit",
                    "desc": "Designed to fit your mouth perfectly for optimal comfort and protection.",
                    "icon": "fa-solid fa-ruler-combined"
                },
                {
                    "title": "Material",
                    "desc": "From soft up to hard acrylic depending on how much you grind.",
                    "icon": "fa-solid fa-layer-group"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "There are many benefits to wearing a night guard which extend well beyond teeth protection.",
            "items": [
                {
                    "title": "Prevents Tooth Damage",
                    "desc": "Night guards protect your teeth from the grinding forces that can wear down enamel, chip teeth or cause cracks.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Reduces Jaw Pain",
                    "desc": "Night guards alleviate jaw muscle tension caused by excessive clenching that may exacerbate a TMJ disorder.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "Improves Sleep Quality",
                    "desc": "Night guards can help relax your jaw, which makes falling asleep and staying asleep at night easier.",
                    "icon": "fa-solid fa-moon"
                },
                {
                    "title": "Cost-Effective",
                    "desc": "The cost of a night guard is less expensive than a crown, veneer, or implant needed to repair damaged teeth.",
                    "icon": "fa-solid fa-coins"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Your dentist can assess if you are a good candidate for a night guard after a thorough evaluation of your symptoms and dental history.",
            "items": [
                {
                    "title": "Teeth Grinders (Bruxism)",
                    "desc": "If you grind or clench your teeth regularly, you may develop enough wear and tear on your enamel to require a night guard.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Patients with TMJ Disorders",
                    "desc": "Those who experience pain in their jaw, or a popping or clicking sound may benefit from a night guard.",
                    "icon": "fa-solid fa-joint"
                },
                {
                    "title": "People with Grinding Induced by Stress",
                    "desc": "Night guards can protect your teeth if you tend toward stress, which is a classic trigger for bruxism.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Individuals with Dental Restorations",
                    "desc": "A night guard can help protect dental restorations from wear and tear caused by grinding.",
                    "icon": "fa-solid fa-tooth"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "Preparation for your night guard appointment guarantees an experience with you in mind from start to finish.",
            "items": [
                {
                    "title": "Talk to your dentist about the signs",
                    "desc": "Discuss any symptoms you may have, such as jaw pain, tooth sensitivity or headaches.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Thoroughly Clean Teeth Prior to Impressions",
                    "desc": "Brush your teeth when the dental impressions are made so that your teeth are molded correctly.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "Bring a List of Medications",
                    "desc": "Some medications can affect your oral health, so you need to give your dentist a current medication list.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "Ask Questions",
                    "desc": "Ask your dentist any questions related to the process, including how to care for your night guard.",
                    "icon": "fa-solid fa-question-circle"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Most people notice the benefits of night guard wear within a few days of usage.",
            "items": [
                {
                    "title": "Reduced Jaw Pain & Discomfort",
                    "desc": "A night guard helps ease tension in the jaw, relieving pain and discomfort from teeth grinding and clenching.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "Protection from Tooth Damage",
                    "desc": "Night guards help keep your teeth safe from enamel wearing down, cracking, and breaking.",
                    "icon": "fa-solid fa-lock"
                },
                {
                    "title": "Improved Sleep Quality",
                    "desc": "A night guard helps you sleep more soundly and without interruptions because it decreases muscle tension in the jaw.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Avoiding Expensive Dental Treatments",
                    "desc": "Regular use prevents teeth and dental work from becoming damaged, saving the patient from costly restorative work.",
                    "icon": "fa-solid fa-piggy-bank"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare",
            "subheading": "",
            "para": "Proper care of your night guard ensures its longevity and effectiveness.",
            "items": [
                {
                    "title": "Daily Cleaning",
                    "desc": "Clean your night guard daily with a toothbrush and toothpaste.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "Proper Storage",
                    "desc": "Store it in a dry, ventilated case to prevent bacteria buildup.",
                    "icon": "fa-solid fa-box-open"
                },
                {
                    "title": "Regular Check-ups",
                    "desc": "Have your dentist check your night guard during regular dental visits for wear and proper fit.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Avoid Hot Water",
                    "desc": "Don't clean your night guard with hot water as it may warp the material.",
                    "icon": "fa-solid fa-temperature-high"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How do I know if I need a night guard?",
                    "answer": "If you wake up with jaw pain, headaches, or notice that your teeth are wearing down or becoming more sensitive, you may benefit from a night guard. Consult your dentist for an evaluation."
                },
                {
                    "question": "Can I buy a night guard over the counter?",
                    "answer": "While over-the-counter night guards are available, they are not custom-fitted, which can make them less comfortable and less effective. A dentist-provided custom night guard offers the best fit and protection."
                },
                {
                    "question": "How long does a night guard last?",
                    "answer": "With proper care, a high-quality night guard can last between 5 and 10 years. However, the lifespan can vary based on the severity of grinding and the type of material used."
                },
                {
                    "question": "How should I clean my night guard?",
                    "answer": "Clean your night guard daily with a toothbrush and toothpaste. Store it in a dry, ventilated case to prevent bacteria buildup."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    ar: {
        meta: {
            title: "",
            description: "",
        },
        breadcrumbs: [],
        hero: undefined,
        howItWorks: undefined,
        procedureSteps: undefined,
        benefits: undefined,
        eligibility: undefined,
        preparationTips: undefined,
        results: undefined,
        aftercareTips: undefined,
        faqData: undefined,
        treatmentAreas1: [],
        treatmentAreas2: []
    },
    shared: {
        images: {
            main: 'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Bwrwanner-Image-8-scaled.jpg',
            beforeAfter: [
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/wrerytj-1.jpg',
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/wrerytj-2.jpg',
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/wrerytj-3.jpg'
            ],
            introductionImage: '',
        },
        product: {
            id: 21,
            title: 'Night Guard',
            price: 299,
            description: 'Protect your teeth from grinding and clenching with our custom night guard.',
        },
    }
};